import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import {
    makeStyles,
    tokens,
    typographyStyles,
    Label,
    Button,
    Tooltip,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItemRadio,
    MessageBar,
    MessageBarBody,
    Link,
} from "@fluentui/react-components";
import {
    ArrowClockwise12Regular,
    ArrowLeft12Regular,
    ArrowSort16Regular,
    DismissCircle12Filled,
    Folder32Regular,
} from "@fluentui/react-icons";
import { IProjectData } from "../../../models/shared/IProjectData";
import { FileWithId } from "../../../models/shared/FileWithId";
import { IProjectsService } from "../../../services/NewformaApi/IProjectsService";
import { useProjectFolderSelection, ProjectFolderErrorContext } from "../../../hooks/useProjectFolderSelection";
import { NewformaSpinner } from "../../_base/Spinner";
import { ProjectFilesBrowserRow } from "./ProjectFilesBrowserRow";
import { ProjectFilesBrowserSkeleton } from "./ProjectFilesBrowserSkeleton";
import { NewformaSharedFolderIcon } from "../../_base/NewformaSharedFolderIcon";
import { ProjectFileItem } from "../../../models/projects/ProjectFilesResponse";
import { ProjectFileNode } from "../../../models/shared/ProjectFileNode";
import { getAttachmentIcon } from "../../shared/attachments/attachmentList/FileTypeIcons";
import { MAX_ATTACHMENTS } from "../../shared/attachments/attachmentList/AttachmentListHelpers";
import { ProjectFolderConnection } from "../../../models/projects/ProjectSettingsResponse";
import { SharePointAuthServiceFactory } from "../../../services/SharePoint/SharePointAuthServiceFactory";
import { ProjectFilesErrorKind, useSharePointRootFolders } from "../../../hooks/useSharePointRootFolders";
import { useSharePointFolderBrowsing } from "../../../hooks/useSharePointFolderBrowsing";
import { SharePointSortOption } from "../../../services/SharePoint/SharePointGraphService";

export interface ProjectFilesBrowserProps extends LocalizeContextProps {
    selectedProject: IProjectData | undefined;
    projectsService: IProjectsService;
    onSelectionChange: (files: FileWithId[]) => void;
    onError?: (kind: ProjectFilesErrorKind, context?: ProjectFolderErrorContext | null) => void;
    maxFiles?: number;
    disabled?: boolean;
    /** Increment to reset nav stack and selections while keeping the current project. */
    resetKey?: number;
    /**
     * Mixed-format lookup set used to prevent re-adding already-attached files. Each entry
     * is one of three key shapes, matching the format built by ProjectFilesSection:
     *   - File NRN (e.g. "nrn:file:abc123")          — same-project flat-file duplicate
     *   - Bare filename (e.g. "report.pdf")            — cross-project same-name duplicate
     *   - displayPath (e.g. "FolderA\\report.pdf")    — folder-fetched file duplicate
     * Do NOT populate this with only NRNs — that silently breaks the other two checks.
     */
    existingFileIds?: ReadonlySet<string>;
    /** Called once (and whenever clearError identity changes) so the parent can hold a reference
     *  to the hook's clearError function and call it when dismissing the error banner. */
    onClearError?: (clearFn: () => void) => void;
    /** Called whenever the browser's loading state changes (initial folder load or per-item async fetch).
     *  Lets the parent disable the attach button while files are being fetched. */
    onLoadingChange?: (isLoading: boolean) => void;
    /** Called synchronously at the start of every user interaction with the list (check/uncheck,
     *  navigate in/out, sort change) so the parent can dismiss a stale error banner. */
    onInteraction?: () => void;
    /** SharePoint Online connections from the project's settings, shown as root folders. */
    sharePointConnections?: ProjectFolderConnection[];
    /** True while the project's settings (and so its SharePoint roots) are still loading. */
    isSharePointConnectionsLoading?: boolean;
    sharePointAuthServiceFactory?: SharePointAuthServiceFactory;
}

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "7px",
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingTop: tokens.spacingVerticalM,
        paddingBottom: tokens.spacingVerticalXS,
    },
    headerLabel: {
        ...typographyStyles.body1,
        color: tokens.colorNeutralForeground1,
    },
    requiredMark: {
        color: tokens.colorPaletteRedForeground3,
        marginLeft: "2px",
    },
    backBar: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
        paddingTop: tokens.spacingVerticalXS,
        paddingBottom: tokens.spacingVerticalXS,
        paddingLeft: tokens.spacingHorizontalS,
        paddingRight: tokens.spacingHorizontalS,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground3,
    },
    backButton: {
        ...typographyStyles.caption1,
        minWidth: "unset",
        flexShrink: 0,
        paddingLeft: "4px",
        paddingRight: "8px",
        height: "24px",
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    folderNameContainer: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalXS,
        flex: 1,
        minWidth: 0,
    },
    currentFolderLabel: {
        ...typographyStyles.body1,
        color: tokens.colorNeutralForeground3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        flex: 1,
    },
    scrollContainer: {
        maxHeight: "240px",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: "2px",
    },
    loadingMore: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: tokens.spacingHorizontalS,
        fontSize: "14px",
        fontWeight: 400,
        color: tokens.colorNeutralForeground3,
        textAlign: "center",
        paddingTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
    },
    loadMoreError: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: tokens.spacingHorizontalXS,
        fontSize: "12px",
        fontWeight: 400,
        color: tokens.colorPaletteRedForeground1,
        textAlign: "center",
        paddingTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
    },
    dot: {
        fontSize: "12px",
        color: tokens.colorNeutralForeground3,
    },
    retryButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        fontSize: "12px",
    },
    endOfList: {
        fontSize: "10px",
        fontWeight: 400,
        color: tokens.colorNeutralForeground3,
        textAlign: "center",
        paddingTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
    },
    emptyState: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground3,
        textAlign: "center",
        padding: tokens.spacingVerticalL,
    },
    noProjectState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: tokens.spacingVerticalS,
        paddingTop: tokens.spacingVerticalXXL,
        paddingBottom: tokens.spacingVerticalXXL,
        textAlign: "center",
    },
    noProjectIcon: {
        color: tokens.colorNeutralForeground3,
        fontSize: "32px",
    },
    noProjectTitle: {
        ...typographyStyles.body1Strong,
        color: tokens.colorNeutralForeground3,
    },
    noProjectSubtitle: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground3,
    },
    backButtonTooltipTarget: {
        display: "inline-flex",
    },
    sortRow: {
        display: "flex",
        justifyContent: "flex-end",
    },
});

/** Bridge: ProjectFilesBrowserRow still uses ProjectFileNode; adapt here. */
function itemToNode(item: ProjectFileItem): ProjectFileNode {
    return { id: item.nrn, name: item.name, type: item.type };
}

const compareNames = (a: ProjectFileNode, b: ProjectFileNode) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

/** Merges SharePoint roots into the server-sorted NPC root list. They have no modified date, so they trail on that sort. */
function mergeRootNodes(
    npcNodes: ProjectFileNode[],
    sharePointNodes: ProjectFileNode[],
    sort: string
): ProjectFileNode[] {
    if (sharePointNodes.length === 0) return npcNodes;
    if (sort === "lastModified") return [...npcNodes, ...sharePointNodes];
    const merged = [...npcNodes, ...sharePointNodes].sort(compareNames);
    return sort === "desc" ? merged.reverse() : merged;
}

const ProjectFilesBrowserInner: React.FC<ProjectFilesBrowserProps> = (props) => {
    const styles = useStyles();
    const disabled = props.disabled ?? false;
    const [sortOption, setSortOption] = useState("asc");

    // Track the last committed project NRN so we can detect project switches.
    // Updated only in the effect below (never during render) so aborted/discarded
    // renders in StrictMode or concurrent mode don't corrupt the ref.
    const committedNrnRef = useRef(props.selectedProject?.nrn);

    // Derive the effective sort value for this render: force "asc" when the
    // incoming project differs from the last committed one.
    const projectChanged = props.selectedProject?.nrn !== committedNrnRef.current;
    const effectiveSortOption = projectChanged ? "asc" : sortOption;

    // After commit, persist the new project NRN and align sort state so
    // subsequent renders use the correct baseline without re-deriving.
    useEffect(() => {
        committedNrnRef.current = props.selectedProject?.nrn;
        if (sortOption !== "asc") {
            setSortOption("asc");
        }
    }, [props.selectedProject?.nrn]);

    const sortLabels: Record<string, string> = {
        asc: "A → Z",
        desc: "Z → A",
        lastModified: "Recently Modified",
    };

    const { onSelectionChange, onError, onInteraction } = props;

    const sharePoint = useSharePointRootFolders({
        connections: props.sharePointConnections ?? [],
        sharePointAuthServiceFactory: props.sharePointAuthServiceFactory,
        onError,
        projectNrn: props.selectedProject?.nrn,
        resetKey: props.resetKey,
    });
    const activeSharePointConnection = sharePoint.activeConnection;
    const inSharePoint = activeSharePointConnection !== null;

    // While browsing SharePoint, sort changes must only hit Graph: hold the NPC hook's sort at the
    // value it last saw so it doesn't refetch /files. It catches up (one fetch) when the user exits.
    // A project switch bypasses the hold: the new project must start on "asc" in a single fetch, since
    // the NPC hook has no stale-response guard and two back-to-back fetches could land out of order.
    const npcSortRef = useRef(effectiveSortOption);
    useEffect(() => {
        if (!inSharePoint) npcSortRef.current = effectiveSortOption;
    }, [inSharePoint, effectiveSortOption]);
    const npcSortOption = inSharePoint && !projectChanged ? npcSortRef.current : effectiveSortOption;

    const {
        browseItems,
        isLoadingBrowse: npcIsLoadingBrowse,
        isLoadingMore: npcIsLoadingMore,
        browseError: npcBrowseError,
        canGoBack: npcCanGoBack,
        currentPath: npcCurrentPath,
        currentFolderKey,
        onFolderClick,
        onGoBack,
        onFileChecked,
        onFolderChecked,
        selectedFiles,
        checkedKeys,
        currentFolderIsSelected,
        loadingKeys,
        lastError,
        lastErrorContext,
        lastErrorSeq,
        clearError,
        hasMoreItems: npcHasMoreItems,
        hasPaginated: npcHasPaginated,
        loadNextPage: npcLoadNextPage,
        retryFetch: npcRetryFetch,
    } = useProjectFolderSelection(
        props.selectedProject?.nrn,
        props.projectsService,
        props.maxFiles,
        props.resetKey,
        props.existingFileIds,
        npcSortOption
    );

    const spBrowsing = useSharePointFolderBrowsing({
        active: activeSharePointConnection,
        sortBy: effectiveSortOption as SharePointSortOption,
        onError,
    });

    // Inside a SharePoint root the list is fed by Graph; everywhere else by the NPC hook. Only the
    // data source changes - header, back bar, sort, rows, paging and error states are shared.
    const isLoadingBrowse = inSharePoint ? spBrowsing.isLoading : npcIsLoadingBrowse;
    const isLoadingMore = inSharePoint ? spBrowsing.isLoadingMore : npcIsLoadingMore;
    const browseError = inSharePoint ? (spBrowsing.loadError ? "load_error" : null) : npcBrowseError;
    const hasMoreItems = inSharePoint ? spBrowsing.hasMoreItems : npcHasMoreItems;
    const hasPaginated = inSharePoint ? spBrowsing.hasPaginated : npcHasPaginated;
    const loadNextPage = inSharePoint ? spBrowsing.loadNextPage : npcLoadNextPage;
    const retryFetch = inSharePoint ? spBrowsing.retry : npcRetryFetch;
    // Items the active source itself returned; excludes the synthetic SharePoint root rows merged in
    // at the NPC root, so error/empty/footer states describe the real listing.
    const sourceItemCount = inSharePoint ? spBrowsing.nodes.length : browseItems.length;
    // Inside SharePoint, Back always has somewhere to go: up a folder, or out to the NPC root list.
    const canGoBack = inSharePoint || npcCanGoBack;
    const currentPath = inSharePoint ? spBrowsing.currentPath : npcCurrentPath;

    const isAtRoot = !npcCanGoBack && !inSharePoint;
    // SharePoint roots come from /settings, so the root listing isn't complete until that resolves.
    const isRootConnectionsLoading = isAtRoot && !!props.isSharePointConnectionsLoading;
    const isSharePointAuthenticating = sharePoint.authenticatingConnectionId !== null;

    // Notify parent of the hook's clearError function so it can call it on banner dismiss.
    useEffect(() => {
        props.onClearError?.(clearError);
    }, [clearError, props.onClearError]);

    // Notify parent whenever loading state changes so it can disable the attach button
    // during initial folder loads and per-item async checkbox fetches.
    useEffect(() => {
        props.onLoadingChange?.(
            isLoadingBrowse || loadingKeys.length > 0 || isRootConnectionsLoading || isSharePointAuthenticating
        );
    }, [
        isLoadingBrowse,
        loadingKeys.length,
        isRootConnectionsLoading,
        isSharePointAuthenticating,
        props.onLoadingChange,
    ]);

    // O(1) lookup by NRN — avoids scanning up to 1000 items on every click.
    const browseItemsMap = useMemo(() => new Map(browseItems.map((item) => [item.nrn, item])), [browseItems]);

    // Root level shows NPC roots plus one folder per SharePoint connection; inside a SharePoint root
    // the Graph listing; deeper NPC levels are NPC only.
    const displayNodes = useMemo(() => {
        if (inSharePoint) return spBrowsing.nodes;
        const npcNodes = browseItems.map(itemToNode);
        return isAtRoot ? mergeRootNodes(npcNodes, sharePoint.rootNodes, effectiveSortOption) : npcNodes;
    }, [inSharePoint, spBrowsing.nodes, browseItems, isAtRoot, sharePoint.rootNodes, effectiveSortOption]);

    // Notify parent whenever the hook's derived file list changes.
    useEffect(() => {
        onSelectionChange(selectedFiles);
    }, [selectedFiles, onSelectionChange]);

    // Forward error events to parent. Dep on lastErrorSeq (not lastError) so this
    // fires even when the same kind is emitted twice in a row.
    useEffect(() => {
        if (lastError != null) onError?.(lastError, lastErrorContext);
    }, [lastErrorSeq]); // intentional: fires on every new error regardless of kind

    const handleCheckboxChange = useCallback(
        (node: ProjectFileNode, checked: boolean) => {
            const item = browseItemsMap.get(node.id);
            if (!item) return;
            onInteraction?.();
            if (item.type === "file") {
                onFileChecked(item, checked);
            } else {
                onFolderChecked(item, checked);
            }
        },
        [browseItemsMap, onFileChecked, onFolderChecked, onInteraction]
    );

    const handleFolderClick = useCallback(
        (node: ProjectFileNode) => {
            if (sharePoint.isSharePointNode(node)) {
                onInteraction?.();
                if (inSharePoint) {
                    spBrowsing.enterFolder(node);
                } else {
                    sharePoint.enter(node);
                }
                return;
            }
            const item = browseItemsMap.get(node.id);
            if (!item) return;
            onInteraction?.();
            onFolderClick(item);
        },
        [
            browseItemsMap,
            onFolderClick,
            onInteraction,
            sharePoint.isSharePointNode,
            sharePoint.enter,
            inSharePoint,
            spBrowsing.enterFolder,
        ]
    );

    const handleGoBack = useCallback(() => {
        onInteraction?.();
        if (inSharePoint) {
            if (!spBrowsing.goBack()) sharePoint.exit();
            return;
        }
        onGoBack();
    }, [onGoBack, onInteraction, inSharePoint, spBrowsing.goBack, sharePoint.exit]);

    const handleSortChange = useCallback(
        (value: string) => {
            onInteraction?.();
            setSortOption(value);
        },
        [onInteraction]
    );

    // Explicit Retry clicks dismiss the dialog's banner so a repeat failure can raise a fresh one;
    // automatic sentinel paging does not go through here.
    const handleRetryFetch = useCallback(() => {
        onInteraction?.();
        retryFetch();
    }, [onInteraction, retryFetch]);
    const handleRetryLoadMore = useCallback(() => {
        onInteraction?.();
        loadNextPage();
    }, [onInteraction, loadNextPage]);

    const loadingKeySet = useMemo(() => new Set(loadingKeys), [loadingKeys]);
    const isFolderLoading = isLoadingBrowse || loadingKeys.length > 0 || isSharePointAuthenticating;
    // maxFiles is the remaining allowance (25 minus files already attached outside the dialog).
    const isLimitReached = selectedFiles.length >= (props.maxFiles ?? MAX_ATTACHMENTS);

    const nodeKey = useCallback(
        (node: ProjectFileNode) => (currentFolderKey ? `${currentFolderKey}\\${node.name}` : node.name),
        [currentFolderKey]
    );

    const selectedFullPaths = useMemo(() => new Set(selectedFiles.map((f) => f.fullPath).filter(Boolean)), [
        selectedFiles,
    ]);

    const isNodeSelected = useCallback(
        (node: ProjectFileNode) => {
            if (currentFolderIsSelected) return true;
            if (node.type === "folder") return checkedKeys.has(nodeKey(node));
            return selectedFullPaths.has(nodeKey(node));
        },
        [currentFolderIsSelected, checkedKeys, selectedFullPaths, nodeKey]
    );

    const isNodePartiallySelected = useCallback(
        (node: ProjectFileNode) => {
            if (node.type !== "folder") return false;
            const key = nodeKey(node);
            if (currentFolderIsSelected || checkedKeys.has(key)) return false;
            const prefix = `${key}\\`;
            return selectedFiles.some((f) => f.fullPath?.startsWith(prefix));
        },
        [currentFolderIsSelected, checkedKeys, nodeKey, selectedFiles]
    );

    const isNodeLoading = useCallback((node: ProjectFileNode) => loadingKeySet.has(nodeKey(node)), [
        loadingKeySet,
        nodeKey,
    ]);

    // Infinite scroll: trigger loadNextPage when the sentinel element becomes visible.
    const observerRef = useRef<IntersectionObserver | null>(null);
    const sentinelRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            if (!node || !hasMoreItems) return;
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0]?.isIntersecting && !isLoadingBrowse && !isLoadingMore && !browseError) {
                        loadNextPage();
                    }
                },
                { threshold: 0.1 }
            );
            observerRef.current.observe(node);
        },
        [hasMoreItems, loadNextPage, isLoadingBrowse, isLoadingMore, browseError]
    );

    const renderHeader = () => (
        <div className={styles.headerRow}>
            <Label className={styles.headerLabel}>
                {props.translate("APP.DIALOG.PROJECT_FILES.SELECT_HEADER").toString()}
                <span className={styles.requiredMark}> *</span>
            </Label>
        </div>
    );

    const renderBackBar = () => {
        if (!props.selectedProject) return null;
        const folderLabel =
            !inSharePoint && currentPath === "/"
                ? props.selectedProject.displayName || props.selectedProject.name
                : currentPath;
        const backButton = (
            <Button
                className={styles.backButton}
                appearance="subtle"
                icon={<ArrowLeft12Regular />}
                disabled={!canGoBack || disabled || isFolderLoading}
                onClick={handleGoBack}
                data-testid="project-files-back-button"
            >
                {props.translate("APP.DIALOG.PROJECT_FILES.BACK").toString()}
            </Button>
        );
        return (
            <div className={styles.backBar} data-testid="project-files-browser-back-bar">
                {!canGoBack && !disabled && !isFolderLoading ? (
                    <Tooltip
                        content={props.translate("APP.DIALOG.PROJECT_FILES.TOPMOST_LEVEL_TOOLTIP").toString()}
                        relationship="description"
                        appearance="inverted"
                        withArrow
                    >
                        <span className={styles.backButtonTooltipTarget}>{backButton}</span>
                    </Tooltip>
                ) : (
                    backButton
                )}
                <div className={styles.folderNameContainer}>
                    {inSharePoint ? (
                        <NewformaSharedFolderIcon
                            tooltip={props.translate("APP.DIALOG.PROJECT_FILES.SHAREPOINT_ONLINE").toString()}
                        />
                    ) : (
                        getAttachmentIcon({ type: "folder", key: "back-bar", name: currentPath })
                    )}
                    <span
                        className={styles.currentFolderLabel}
                        title={folderLabel}
                        data-testid="project-files-browser-folder-label"
                    >
                        {folderLabel}
                    </span>
                </div>
            </div>
        );
    };

    const renderSort = () => {
        if (
            !props.selectedProject ||
            isLoadingBrowse ||
            isRootConnectionsLoading ||
            browseError ||
            displayNodes.length === 0
        )
            return null;

        return (
            <div className={styles.sortRow}>
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <Button appearance="subtle" size="small" icon={<ArrowSort16Regular />} disabled={disabled}>
                            {sortLabels[effectiveSortOption]}
                        </Button>
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList
                            checkedValues={{ sort: [effectiveSortOption] }}
                            onCheckedValueChange={(_e, data) => handleSortChange(data.checkedItems[0])}
                        >
                            <MenuItemRadio name="sort" value="asc">
                                A → Z
                            </MenuItemRadio>
                            <MenuItemRadio name="sort" value="desc">
                                Z → A
                            </MenuItemRadio>
                            <MenuItemRadio name="sort" value="lastModified">
                                {props.translate("APP.DIALOG.PROJECT_FILES.SORT_LAST_MODIFIED").toString()}
                            </MenuItemRadio>
                        </MenuList>
                    </MenuPopover>
                </Menu>
            </div>
        );
    };

    const renderBody = () => {
        if (!props.selectedProject) {
            return (
                <div className={styles.noProjectState} data-testid="project-files-browser-no-project">
                    <Folder32Regular className={styles.noProjectIcon} />
                    <span className={styles.noProjectTitle}>
                        {props.translate("APP.DIALOG.PROJECT_FILES.NO_PROJECT_TITLE").toString()}
                    </span>
                    <span className={styles.noProjectSubtitle}>
                        {props.translate("APP.DIALOG.PROJECT_FILES.NO_PROJECT_SUBTITLE").toString()}
                    </span>
                </div>
            );
        }
        if ((isLoadingBrowse && !isLoadingMore) || isRootConnectionsLoading) return <ProjectFilesBrowserSkeleton />;
        if (browseError && sourceItemCount === 0) {
            return (
                <div className={styles.loadMoreError} data-testid="project-files-browser-load-error">
                    <DismissCircle12Filled />
                    {props.translate("APP.DIALOG.PROJECT_FILES.PROGRESSIVE_LOADING.ERROR").toString()}
                    <span className={styles.dot}>·</span>
                    <Link
                        className={styles.retryButton}
                        onClick={handleRetryFetch}
                        data-testid="project-files-browser-load-error-retry"
                    >
                        <ArrowClockwise12Regular />
                        {props.translate("APP.DIALOG.PROJECT_FILES.PROGRESSIVE_LOADING.RETRY").toString()}
                    </Link>
                </div>
            );
        }
        if (displayNodes.length === 0) {
            return (
                <span className={styles.emptyState} data-testid="project-files-browser-empty">
                    {props.translate("APP.DIALOG.PROJECT_FILES.EMPTY_FOLDER").toString()}
                </span>
            );
        }
        return (
            <div className={styles.scrollContainer}>
                {displayNodes.map((node, index) => {
                    if (sharePoint.isSharePointNode(node)) {
                        // Browse-only by design: the transfer API can't resolve SharePoint items yet, so
                        // selection/attach is a separate story (doc/npc-32337-sharepoint-msal-naa-auth.md).
                        return (
                            <ProjectFilesBrowserRow
                                key={node.id}
                                index={index}
                                node={node}
                                isSelected={false}
                                isLoading={!inSharePoint && sharePoint.authenticatingConnectionId === node.connectionId}
                                disabled={disabled || isFolderLoading}
                                hideCheckbox={true}
                                sharePointTooltip={props
                                    .translate("APP.DIALOG.PROJECT_FILES.SHAREPOINT_ONLINE")
                                    .toString()}
                                onCheckboxChange={handleCheckboxChange}
                                onFolderClick={handleFolderClick}
                            />
                        );
                    }
                    const isSelected = isNodeSelected(node);
                    return (
                        <ProjectFilesBrowserRow
                            key={nodeKey(node)}
                            index={index}
                            node={node}
                            isSelected={isSelected}
                            isPartiallySelected={isNodePartiallySelected(node)}
                            isLoading={isNodeLoading(node)}
                            disabled={disabled || isFolderLoading}
                            hideCheckbox={!canGoBack && node.type === "folder"}
                            checkboxDisabled={
                                (!canGoBack && node.type === "folder") ||
                                (isLimitReached && !isSelected) ||
                                (isFolderLoading && !isNodeLoading(node))
                            }
                            selectedFolderTooltip={props
                                .translate("APP.DIALOG.PROJECT_FILES.SELECTED_FOLDER_TOOLTIP")
                                .toString()}
                            sharePointTooltip={props.translate("APP.DIALOG.PROJECT_FILES.SHAREPOINT_ONLINE").toString()}
                            onCheckboxChange={handleCheckboxChange}
                            onFolderClick={handleFolderClick}
                        />
                    );
                })}
                {hasMoreItems && !browseError && (
                    <div
                        ref={sentinelRef}
                        className={styles.loadingMore}
                        data-testid="project-files-browser-loading-more"
                    >
                        <NewformaSpinner size="tiny" />
                        {props.translate("APP.DIALOG.PROJECT_FILES.PROGRESSIVE_LOADING.MORE").toString()}
                    </div>
                )}
                {browseError && (
                    <div className={styles.loadMoreError} data-testid="project-files-browser-load-more-error">
                        <DismissCircle12Filled />
                        {props.translate("APP.DIALOG.PROJECT_FILES.PROGRESSIVE_LOADING.ERROR").toString()}
                        <span className={styles.dot}>·</span>
                        <Link
                            className={styles.retryButton}
                            onClick={handleRetryLoadMore}
                            data-testid="project-files-browser-retry"
                        >
                            <ArrowClockwise12Regular />
                            {props.translate("APP.DIALOG.PROJECT_FILES.PROGRESSIVE_LOADING.RETRY").toString()}
                        </Link>
                    </div>
                )}
                {!hasMoreItems && hasPaginated && !isLoadingBrowse && sourceItemCount > 0 && !browseError && (
                    <div className={styles.endOfList} data-testid="project-files-browser-end-of-list">
                        {props
                            .translate("APP.DIALOG.PROJECT_FILES.PROGRESSIVE_LOADING.NO_MORE")
                            .toString()
                            .replace("{{count}}", sourceItemCount.toString())}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.container}>
            {renderHeader()}
            {renderBackBar()}
            {renderSort()}
            {renderBody()}
        </div>
    );
};

export const ProjectFilesBrowser = withLocalize(ProjectFilesBrowserInner);
